import React from 'react';
import { Check, X } from 'lucide-react';
import PrecioDobleCell from './PrecioDobleCell';

const CAMPOS_PRECIOS = [
  { key: 'precio_lista', label: 'Precio 1' },
  { key: 'precio1', label: 'Precio 2' },
  { key: 'precio2', label: 'Precio 3' },
  { key: 'precio3', label: 'Precio 4' },
  { key: 'precio4', label: 'Precio 5' },
  { key: 'precio5', label: 'Precio 6' },
  { key: 'precio6', label: 'Precio 7' }
];

const PreciosRow = React.memo(({
  producto,
  rowIndex,
  editedData,
  porcentajesData,
  isEdited,
  // Stable callbacks (useCallback from parent)
  getCosto,
  getValor,
  getPorcentaje,
  getCantidadDescuento,
  getPorcentajeBase,
  handleCostoChange,
  handleDescuentoChange,
  handlePorcentajeBaseChange,
  handlePorcentajeChange,
  handlePrecioChange,
  handleKeyNavigation,
  guardarProducto,
  cancelarCambios,
  guardando,
  // Drag-to-fill
  onDragHandleMouseDown,
  isDragHighlighted,
  dragOriginRow
}) => {
  const costo = getCosto(producto);
  const cantidadDescuento = getCantidadDescuento(producto);
  const porcentajeBaseRaw = getPorcentajeBase(producto);
  const porcentajeBase = Math.abs(porcentajeBaseRaw - Math.round(porcentajeBaseRaw)) < 0.05
    ? Math.round(porcentajeBaseRaw)
    : Math.round(porcentajeBaseRaw * 100) / 100;

  // Precio publico calculation
  const precioLista = getValor(producto, 'precio_lista');
  let precioPublico;
  if (precioLista > 0 && porcentajeBase > 0) {
    precioPublico = (Math.round(precioLista * (1 + porcentajeBase / 100) * 100) / 100).toFixed(2);
  } else {
    precioPublico = precioLista > 0 ? precioLista.toFixed(2) : '0.00';
  }

  const rowClassName = [
    isEdited ? 'editado' : '',
    isDragHighlighted ? 'drag-highlight' : '',
    dragOriginRow === rowIndex ? 'drag-origin' : ''
  ].filter(Boolean).join(' ') || undefined;

  return (
    <tr className={rowClassName} data-row={rowIndex}>
      <td className="col-codigo">{producto.codigo_corto || '-'}</td>
      <td className="col-descripcion" title={producto.nombre}>
        {producto.nombre?.substring(0, 40)}
        {producto.nombre?.length > 40 ? '...' : ''}
        {producto.equivalencia && (
          <span className="equivalencia-badge" title={`Equivale a ${producto.equivalencia} unidades base`}>
            x{producto.equivalencia}
          </span>
        )}
      </td>
      <td className="col-costo" style={{ position: 'relative' }}>
        <input
          type="number"
          min="0"
          step="0.01"
          value={getValor(producto, 'costo_precios')}
          onChange={(e) => handleCostoChange(producto.id, e.target.value)}
          onKeyDown={(e) => handleKeyNavigation(e, rowIndex, 'costo')}
          onFocus={(e) => e.target.select()}
          className="input-costo"
          data-row={rowIndex}
          data-col="costo"
          data-producto-id={producto.id}
          data-field-type="costo"
        />
        {onDragHandleMouseDown && (
          <div
            className="drag-fill-handle"
            onMouseDown={(e) => onDragHandleMouseDown(e, rowIndex, 'costo', producto.id)}
            title="Arrastrar para copiar"
          />
        )}
      </td>
      <td className="col-cantidad" style={{ position: 'relative' }}>
        <input
          type="number"
          min="0"
          step="1"
          value={cantidadDescuento}
          onChange={(e) => handleDescuentoChange(producto.id, e.target.value)}
          onKeyDown={(e) => handleKeyNavigation(e, rowIndex, 'cantidad_descuento')}
          onFocus={(e) => e.target.select()}
          className="input-cantidad"
          title={producto.producto_base_id ? 'Cantidad descuento del producto base' : 'Cantidad descuento'}
          data-row={rowIndex}
          data-col="cantidad_descuento"
          data-producto-id={producto.id}
          data-field-type="cantidad_descuento"
        />
        {onDragHandleMouseDown && (
          <div
            className="drag-fill-handle"
            onMouseDown={(e) => onDragHandleMouseDown(e, rowIndex, 'cantidad_descuento', producto.id)}
            title="Arrastrar para copiar"
          />
        )}
      </td>
      <td className="col-porcentaje-desc" style={{ display: 'none' }} />
      <td className="col-porcentaje-base" style={{ position: 'relative' }}>
        <input
          type="number"
          step="0.1"
          value={porcentajeBase}
          onChange={(e) => handlePorcentajeBaseChange(producto.id, e.target.value)}
          onKeyDown={(e) => handleKeyNavigation(e, rowIndex, 'porcentaje_base')}
          onFocus={(e) => e.target.select()}
          className="input-porcentaje-base"
          placeholder="%"
          title={producto.equivalencia && producto.precio_base_lista
            ? `Base: $${producto.precio_base_lista} * ${producto.equivalencia} = $${(producto.precio_base_lista * producto.equivalencia).toFixed(2)}`
            : '% a subir para base'
          }
          data-row={rowIndex}
          data-col="porcentaje_base"
          data-producto-id={producto.id}
          data-field-type="porcentaje_base"
        />
        {onDragHandleMouseDown && (
          <div
            className="drag-fill-handle"
            onMouseDown={(e) => onDragHandleMouseDown(e, rowIndex, 'porcentaje_base', producto.id)}
            title="Arrastrar para copiar"
          />
        )}
      </td>
      <td className="col-precio-publico" style={{ position: 'relative' }}>
        <input
          type="text"
          inputMode="decimal"
          value={precioPublico}
          onChange={(e) => {
            const pp = parseFloat(e.target.value) || 0;
            if (precioLista > 0 && pp > 0) {
              const nuevoIncre = Math.round(((pp - precioLista) / precioLista) * 100 * 100) / 100;
              handlePorcentajeBaseChange(producto.id, nuevoIncre.toString());
            }
          }}
          onKeyDown={(e) => handleKeyNavigation(e, rowIndex, 'precio_publico')}
          onFocus={(e) => e.target.select()}
          className="input-precio-publico"
          title="Precio publico = P.Lista + %Incre"
          data-row={rowIndex}
          data-col="precio_publico"
          data-producto-id={producto.id}
          data-field-type="precio_publico"
        />
        {onDragHandleMouseDown && (
          <div
            className="drag-fill-handle"
            onMouseDown={(e) => onDragHandleMouseDown(e, rowIndex, 'precio_publico', producto.id)}
            title="Arrastrar para copiar"
          />
        )}
      </td>
      {CAMPOS_PRECIOS.map(campo => (
        <PrecioDobleCell
          key={campo.key}
          productoId={producto.id}
          campoKey={campo.key}
          rowIndex={rowIndex}
          porcentaje={getPorcentaje(producto.id, campo.key)}
          precio={getValor(producto, campo.key)}
          hidden={campo.key === 'precio6'}
          onPorcentajeChange={handlePorcentajeChange}
          onPrecioChange={handlePrecioChange}
          onKeyNavigation={handleKeyNavigation}
          onDragHandleMouseDown={onDragHandleMouseDown}
        />
      ))}
      <td className="col-acciones">
        {isEdited && (
          <>
            <button
              className="btn-accion guardar"
              onClick={() => guardarProducto(producto.id)}
              disabled={guardando}
              title="Guardar"
            >
              <Check size={14} />
            </button>
            <button
              className="btn-accion cancelar"
              onClick={() => cancelarCambios(producto.id)}
              disabled={guardando}
              title="Cancelar"
            >
              <X size={14} />
            </button>
          </>
        )}
      </td>
    </tr>
  );
}, (prev, next) => {
  // Only re-render if data for THIS row changed
  if (prev.editedData !== next.editedData) return false;
  if (prev.porcentajesData !== next.porcentajesData) return false;
  if (prev.isEdited !== next.isEdited) return false;
  if (prev.rowIndex !== next.rowIndex) return false;
  if (prev.producto !== next.producto) return false;
  if (prev.guardando !== next.guardando) return false;
  if (prev.isDragHighlighted !== next.isDragHighlighted) return false;
  if (prev.dragOriginRow !== next.dragOriginRow) return false;
  return true;
});

PreciosRow.displayName = 'PreciosRow';

export default PreciosRow;
